import React from 'react';
import Button from '../ui/Button';
import Icon from '../AppIcon';

const PlanComparisonModal = ({ isOpen, onClose, onSelectPlan }) => {
  if (!isOpen) return null;

  const features = [
    {
      category: "AI Reviews",
      items: [
        { name: "AI-powered review improvements", free: true, pro: true },
        { name: "Monthly AI review limit", free: "2 reviews", pro: "Unlimited" },
        { name: "Review response suggestions", free: true, pro: true },
        { name: "Sentiment analysis", free: true, pro: true }
      ]
    },
    {
      category: "Campaign Management",
      items: [
        { name: "Campaign builder", free: true, pro: true },
        { name: "Email & SMS review requests", free: true, pro: true },
        { name: "Custom thank you pages", free: true, pro: true },
        { name: "External platform integration", free: true, pro: true },
        { name: "A/B testing campaigns", free: false, pro: true }
      ]
    },
    {
      category: "Analytics & Reporting",
      items: [
        { name: "Basic analytics dashboard", free: true, pro: true },
        { name: "Review trends and insights", free: true, pro: true },
        { name: "Customer feedback reports", free: true, pro: true },
        { name: "Custom report exports", free: false, pro: true },
        { name: "Real-time analytics", free: false, pro: true }
      ]
    },
    {
      category: "Support & Services",
      items: [
        { name: "Email support", free: true, pro: true },
        { name: "Knowledge base access", free: true, pro: true },
        { name: "Priority support", free: false, pro: true },
        { name: "Phone support", free: false, pro: true },
        { name: "Dedicated account manager", free: false, pro: true },
        { name: "Custom onboarding", free: false, pro: true }
      ]
    },
    {
      category: "Customization",
      items: [
        { name: "Basic branding", free: true, pro: true },
        { name: "Custom email templates", free: true, pro: true },
        { name: "Custom integrations", free: false, pro: true },
        { name: "API access", free: false, pro: true }
      ]
    }
  ];

  const plans = [
    {
      id: 'free',
      name: 'Free Plan',
      price: '$0',
      period: 'forever',
      description: 'Perfect for getting started',
      highlight: false,
      trial: false
    },
    {
      id: 'pro',
      name: 'Pro Plan',
      price: '$97',
      period: 'per month',
      description: 'Scale without limits',
      highlight: true,
      trial: false
    }
  ];

  const FeatureIcon = ({ included, value }) => {
    if (value && typeof value === 'string') {
      return <span className="text-text-secondary text-sm font-medium">{value}</span>;
    }
    
    return included ? (
      <Icon name="Check" size={20} className="text-green-500" />
    ) : (
      <Icon name="X" size={20} className="text-gray-300" />
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-border">
          <div>
            <h2 className="text-2xl font-bold text-text-primary">Compare Plans</h2>
            <p className="text-text-secondary">Choose the plan that's right for your business</p>
          </div>
          <Button variant="ghost" onClick={onClose} className="p-2">
            <Icon name="X" size={24} />
          </Button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* Plan Headers */}
          <div className="grid grid-cols-3 gap-4 p-6 border-b border-border bg-surface-secondary">
            <div></div>
            {plans.map((plan) => (
              <div key={plan.id} className={`text-center p-4 rounded-lg ${plan.highlight ? 'bg-primary/5 border-2 border-primary' : 'bg-surface'}`}>
                {plan.highlight && (
                  <div className="inline-block bg-primary text-white px-2 py-1 rounded-full text-xs font-medium mb-2">
                    Most Popular
                  </div>
                )}
                <h3 className="text-xl font-bold text-text-primary mb-1">{plan.name}</h3>
                <div className="mb-2">
                  <span className="text-2xl font-bold text-text-primary">{plan.price}</span>
                  <span className="text-text-secondary text-sm">/{plan.period}</span>
                </div>
                {plan.trial && (
                  <div className="text-green-600 text-sm font-medium mb-2">{plan.trial}</div>
                )}
                <p className="text-text-secondary text-sm">{plan.description}</p>
              </div>
            ))}
          </div>

          {/* Feature Comparison */}
          <div className="p-6">
            {features.map((category, categoryIndex) => (
              <div key={categoryIndex} className="mb-8">
                <h4 className="text-lg font-semibold text-text-primary mb-4 px-4">
                  {category.category}
                </h4>
                <div className="space-y-2">
                  {category.items.map((item, itemIndex) => (
                    <div key={itemIndex} className="grid grid-cols-3 gap-4 py-3 px-4 hover:bg-surface-secondary rounded-lg transition-colors">
                      <div className="text-text-secondary font-medium">
                        {item.name}
                      </div>
                      <div className="flex justify-center">
                        <FeatureIcon included={item.free === true} value={typeof item.free === 'string' ? item.free : null} />
                      </div>
                      <div className="flex justify-center">
                        <FeatureIcon included={item.pro === true} value={typeof item.pro === 'string' ? item.pro : null} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-border p-6 bg-surface-secondary">
          <div className="grid grid-cols-3 gap-4">
            <div></div>
            {plans.map((plan) => (
              <div key={plan.id} className="text-center">
                <Button
                  onClick={() => {
                    onSelectPlan(plan.id);
                    onClose();
                  }}
                  variant={plan.highlight ? "default" : "outline"}
                  className="w-full"
                >
                  Select {plan.name}
                </Button>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-4">
            <p className="text-text-muted text-sm">
              You can upgrade or downgrade your plan at any time. No long-term commitments.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanComparisonModal;